import { gql } from '@apollo/client';
import * as MENUS from '../constants/menus';
import { BlogInfoFragment } from '../fragments/GeneralSettings';
// MediaBoosterSettingsFragment temporarily removed due to GraphQL schema issues
import {
  Header,
  Footer,
  Main,
  Container,
  ContentWrapper,
  SEO,
} from '../components';

export default function Avtalevilkar({ data, sharedData }) {
  if (!data) return <div>Error loading page</div>;

  const { title: siteTitle, description: siteDescription } = data?.generalSettings ?? {};
  const primaryMenu = data?.headerMenuItems?.nodes ?? [];
  const footerMenu = data?.footerMenuItems?.nodes ?? [];
  // Fallback logos until MediaBooster settings are configured
  const lightLogo = null; // Header component will use fallback
  
  const page = data?.page ?? {};
  const { title, content, seo } = page;

  return (
    <>
      <SEO 
        seo={{
          title: `${title || 'Avtalevilkår'} - ${siteTitle}`,
          description: seo?.description || 'Les våre avtalevilkår og vilkår for samarbeid med Mediabooster Norge AS.',
          canonicalUrl: 'https://mediabooster.no/avtalevilkar/',
          openGraph: {
            title: `${title || 'Avtalevilkår'} - ${siteTitle}`,
            description: seo?.description || 'Les våre avtalevilkår og vilkår for samarbeid med Mediabooster Norge AS.',
            url: 'https://mediabooster.no/avtalevilkar/',
            siteName: siteTitle,
            type: 'website'
          }
        }}
      />
      <Header 
        title={siteTitle} 
        description={siteDescription} 
        menuItems={primaryMenu} 
        lightLogo={lightLogo}
      />
      <Main style={{ paddingTop: 'var(--wpe--header--spacing)' }}>
        <Container>
          <ContentWrapper>
            <div className="page-content">
              {title && <h1>{title}</h1>}
              {content && (
                <div 
                  className="content" 
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              )}
            </div>
          </ContentWrapper>
        </Container>
      </Main>
      <Footer 
        title={siteTitle} 
        menuItems={primaryMenu}
        settings={data}
        sharedData={sharedData}
      />
    </>
  );
}

export async function getStaticProps() {
  const { getApolloClient } = await import('@faustwp/core');
  const apolloClient = getApolloClient();
  
  try {
    // Main page data query
    const { data: pageData } = await apolloClient.query({
      query: gql`
        ${BlogInfoFragment}
        query GetAvtalevilkarPage(
          $headerLocation: MenuLocationEnum,
          $footerLocation: MenuLocationEnum
        ) {
          page(id: "16723", idType: DATABASE_ID) {
            id
            title
            content
            seo {
              title
              description
              canonicalUrl
              openGraph {
                title
                description
                url
                siteName
                type
              }
            }
          }
          generalSettings { ...BlogInfoFragment }
          headerMenuItems: menuItems(where: { location: $headerLocation }) {
            nodes { id parentId label path target title }
          }
          footerMenuItems: menuItems(where: { location: $footerLocation }) {
            nodes { id parentId label path target title }
          }
        }
      `,
      variables: {
        headerLocation: MENUS.PRIMARY_LOCATION,
        footerLocation: MENUS.FOOTER_LOCATION,
      },
      fetchPolicy: 'no-cache'
    });

    // Shared data query for Footer
    const { data: sharedData } = await apolloClient.query({
      query: gql`
        query GetSharedAvtalevilkarData {
          # Footer data - exact same query as Footer component
          tjenester(first: 100, where: { taxQuery: { taxArray: { terms: "4", field: TAXONOMY_ID } } }) {
            nodes {
              id
              title
              slug
              uri
              terms {
                nodes {
                  id
                  name
                }
              }
              tjenesterPost {
                serviceDescription
              }
            }
          }
          
          # MediaBooster settings - includes Footer data
          mediaboosterSettings {
            mediaboosterSettingsFields {
              nyhetsbrev {
                tittelNyhetsbrev
                innholdNyhetsbrev
              }
            }
          }
        }
      `,
      fetchPolicy: 'no-cache'
    });
    
    return {
      props: {
        data: pageData,
        sharedData: sharedData
      },
      revalidate: 60 * 15, // Revalidate every 15 minutes
    };
  } catch (error) {
    console.error('Error fetching avtalevilkar data:', error);
    return {
      props: {
        data: {},
        sharedData: {}
      },
      revalidate: 60 * 5, // Retry more frequently on error
    };
  }
}
